import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      // Even on error we show the generic success message to avoid enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0b0a10] text-white overflow-hidden flex flex-col">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-15%] left-[-10%] w-[700px] h-[700px] rounded-full bg-violet-600/20 blur-[140px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-fuchsia-500/15 blur-[140px]" />
      </div>

      <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="forgot-brand">
          <img src="/logo.png" alt="The Trading Dock" className="w-11 h-11 rounded-2xl ring-1 ring-white/15" />
          <div className="leading-tight">
            <div className="text-base sm:text-lg font-black tracking-tight">THE TRADING DOCK<sup className="text-[9px] opacity-60 ml-0.5">®</sup></div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">finance</div>
          </div>
        </Link>
        <Link to="/login" data-testid="forgot-back" className="text-xs sm:text-sm text-slate-300 hover:text-white border border-white/10 hover:border-white/25 px-3 sm:px-4 py-2 rounded-full transition-colors">
          ← Volver
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8">
        <div className="w-full max-w-md rounded-3xl bg-[#131218]/70 backdrop-blur-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] p-7 sm:p-9">
          {!sent ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300/80 font-bold mb-2">RECUPERAR ACCESO</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]" data-testid="forgot-title">
                Restablecer<br /><span className="brand-mark">contraseña.</span>
              </h1>
              <p className="mt-3 text-sm text-slate-400">Te enviaremos un enlace seguro a tu correo. Expira en 30 minutos.</p>

              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="forgot-form">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Correo registrado</div>
                  <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 focus-within:border-violet-500/60 focus-within:bg-white/[0.06] rounded-xl px-4 py-3 transition-all">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <input
                      data-testid="forgot-email-input"
                      type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="w-full bg-transparent outline-none text-white placeholder:text-slate-600 text-sm"
                    />
                  </div>
                </label>
                <button
                  type="submit" disabled={loading || !email}
                  data-testid="forgot-submit"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white font-semibold py-3.5 rounded-xl transition-all shadow-[0_10px_40px_-12px_rgba(139,92,246,0.7)] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enviar enlace <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-emerald-300" />
              </div>
              <h1 className="mt-5 text-2xl sm:text-3xl font-black tracking-tight">Revisa tu correo</h1>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Si <span className="text-slate-200 font-medium">{email}</span> existe en nuestra base, te enviamos un enlace para crear una nueva contraseña.
                <br /><br />Si no lo ves en 2 minutos, revisa la carpeta de spam.
              </p>
              <Link to="/login" className="mt-7 inline-block text-violet-300 hover:text-violet-200 font-medium text-sm" data-testid="forgot-return">
                ← Volver al inicio de sesión
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
